/* eslint-disable */
import React from "react";
import { Card, Grid, Text, createStyles, Loader, Center } from "@mantine/core";
import {
  DashboardStatistic,
  useGetDashboardStatistics,
} from "api/attendance/dashboardStatistics";
import { UseQueryResult } from "@tanstack/react-query";
import { useGetTimeUtilization } from "api/attendance/gettodayTimeUtilization";

const useStyles = createStyles((theme) => ({
  card: {
    height: "100%",
    borderRadius: 8,
    padding: 18,
    border: `1px solid ${theme.colors.gray[3]}`,
    background: theme.white,
    boxShadow: "0 6px 18px rgba(12,20,40,0.04)",
    // boxShadow: "0 6px 18px rgba(12,20,40,0.06)",
    // background: "#fff",
  },
  metricTitle: {
    color: "#6b7280",
    fontSize: 13,
    marginBottom: 6,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 800,
    color: "#0f172a",
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    display: "inline-block",
    marginRight: 8,
  },
  timelineWrap: {
    marginTop: 18,
    padding: "18px 10px",
    background: "#fff",
  },
  timelineBar: {
    position: "relative",
    height: 28,
    background: "rgba(15,23,42,0.04)",
    borderRadius: 8,
    overflow: "hidden",
  },
  segment: {
    position: "absolute",
    top: 3,
    bottom: 3,
    borderRadius: 6,
    boxShadow: "0 2px 0 rgba(0,0,0,0.04) inset",
  },
  timeLabels: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 8,
    color: "#6b7280",
    fontSize: 12,
  },
  metricsGrid: {
    marginBottom: 8,
  },
}));

type Segment = {
  start: string;
  end: string;
  color?: string;
  label?: string;
};

export default function AttendanceTimeline({
  startHour = 9,
  endHour = 18,
}: {
  startHour?: number;
  endHour?: number;
}) {
  const { classes } = useStyles();

  const { data: payload, isLoading } =
    useGetDashboardStatistics() as UseQueryResult<DashboardStatistic, unknown>;
  const { data: timedata } = useGetTimeUtilization();
  const formatHours = (decimalHours?: number) => {
    if (!decimalHours || decimalHours <= 0) return "0h 0m";
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const todayTotalHours: number =
    Number((payload as any)?.today?.totalWorkingHours) || 0;
  const overtimeHours: number = Number((payload as any)?.overtime?.hours) || 0;
  const thisMonthTotalHours: number =
    Number((payload as any)?.thisMonth?.totalWorkingHours) || 0;
  const remainingHours: number =
    Number((payload as any)?.remaining?.hours) || 0;

  const metricsData = [
    {
      title: "Today's Working Hours",
      value: formatHours(todayTotalHours),
      color: "#e5e7eb",
    },
    {
      title: "This Month Working Hours",
      value: formatHours(thisMonthTotalHours),
      color: "#10b981",
    },
    {
      title: "Overtime Hours",
      value: formatHours(overtimeHours),
      color: "#2563eb",
    },
    {
      title: "Remaining Hours",
      value: formatHours(remainingHours),
      color: "#f59e0b",
    },
  ];

  const hoursToMinutes = (h: number) => Math.round(h * 60);

  const windowMinutes = Math.max(0, endHour - startHour) * 60;
  let todayHours = timedata?.data?.workingHours ?? 0;
  let productiveMinutes = hoursToMinutes(todayHours);
  let otMinutes = hoursToMinutes(overtimeHours);

  if (productiveMinutes > windowMinutes) {
    productiveMinutes = windowMinutes;
    otMinutes = 0;
  } else if (productiveMinutes + otMinutes > windowMinutes) {
    otMinutes = Math.max(0, windowMinutes - productiveMinutes);
  }

  const minutesFromStartToTime = (minutesFromStart: number) => {
    const total = startHour * 60 + minutesFromStart;
    const hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const builtSegments: Segment[] = [];
  let cursor = 0;

  if (productiveMinutes > 0) {
    const segStart = minutesFromStartToTime(cursor);
    const segEnd = minutesFromStartToTime(cursor + productiveMinutes);
    builtSegments.push({
      start: segStart,
      end: segEnd,
      color: "#10b981",
      label: "Productive",
    });
    cursor += productiveMinutes;
  }

  if (otMinutes > 0) {
    const segStart = minutesFromStartToTime(cursor);
    const segEnd = minutesFromStartToTime(cursor + otMinutes);
    builtSegments.push({
      start: segStart,
      end: segEnd,
      color: "#2563eb",
      label: "Overtime",
    });
    cursor += otMinutes;
  }

  const remainingWindow = Math.max(0, windowMinutes - cursor);
  if (remainingWindow > 0) {
    const segStart = minutesFromStartToTime(cursor);
    const segEnd = minutesFromStartToTime(cursor + remainingWindow);
    builtSegments.push({
      start: segStart,
      end: segEnd,
      color: "#e5e7eb",
      label: "Idle",
    });
  }

  const segments: Segment[] = builtSegments.filter((s) => s.start !== s.end);

  const totalMinutes = windowMinutes || (endHour - startHour) * 60;

  const timeToMinutes = (t: string) => {
    const [hh, mm] = t.split(":").map(Number);
    return hh * 60 + mm;
  };

  const toPercent = (start: string, end: string) => {
    const s = timeToMinutes(start);
    const e = timeToMinutes(end);
    const startMin = startHour * 60;
    const p1 = ((s - startMin) / totalMinutes) * 100;
    const p2 = ((e - startMin) / totalMinutes) * 100;
    return {
      left: Math.max(0, p1),
      width: Math.max(0, p2 - p1),
    };
  };

  const convertToAMPM = (time24: string) => {
    const [hour, minute] = time24.split(":");
    const h = +hour % 12 || 12;
    const ampm = +hour < 12 ? "AM" : "PM";
    return `${h.toString().padStart(2, "0")}:${minute} ${ampm}`;
  };
  const hours = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);

  if (isLoading) {
    return (
      <Card className={classes.card}>
        <Center py="lg">
          <Loader size="sm" />
          <Text ml="sm">Loading attendance statistics...</Text>
        </Center>
      </Card>
    );
  }

  if (!payload) {
    return (
      <Card className={classes.card}>
        <Center py="lg">
          <Text>No attendance data available.</Text>
        </Center>
      </Card>
    );
  }

  return (
    <Card className={classes.card} radius="md">
      <Grid gutter={18} align="center" className={classes.metricsGrid}>
        {metricsData.map((m) => (
          <Grid.Col span={3} key={m.title}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className={classes.dot} style={{ background: m.color }} />
                <Text className={classes.metricTitle}>{m.title}</Text>
              </div>
              <div>
                <Text className={classes.metricValue}>{m.value}</Text>
              </div>
            </div>
          </Grid.Col>
        ))}
      </Grid>

      {segments.length > 0 && (
        <div className={classes.timelineWrap}>
          <div style={{ padding: "8px 6px" }}>
            <div className={classes.timelineBar}>
              {segments.map((seg, idx) => {
                const { left, width } = toPercent(seg.start, seg.end);
                const l = Math.max(0, Math.min(100, left));
                const w = Math.max(0, Math.min(100 - l, width));
                return (
                  <div
                    key={`${seg.label ?? "seg"}-${idx}`}
                    className={classes.segment}
                    title={`${seg.label ?? ""} ${seg.start} - ${seg.end}`}
                    style={{
                      left: `${l}%`,
                      width: `${w}%`,
                      background: seg.color ?? "#ccc",
                    }}
                  />
                );
              })}
            </div>

            <div className={classes.timeLabels}>
              {hours.map((h, i) => (
                <div
                  key={h}
                  style={{
                    width: 1,
                    textAlign:
                      i === 0
                        ? "left"
                        : i === hours.length - 1
                        ? "right"
                        : "center",
                  }}
                >
                  {convertToAMPM(`${String(h).padStart(2, "0")}:00`)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
